import { useRef, useEffect } from "react"
import { Loader2 } from "lucide-react"
import { useTheme } from "@/context/theme/theme-context"
import hive from "@/lib/api/hive"
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom" // Add useLocation
import { ThemeProvider } from "@/context/theme/theme-provider.tsx"
import { CurrencyProvider } from "@/context/currency/currency-provider.tsx"
import { AuthProvider } from "@/context/auth/auth-provider" // Import Provider
import { useAuth } from "@/context/auth/auth-context" // Import Hook
import { useCurrency } from "@/context/currency/currency-context"
import { isCurrencyCode, normalizeCurrencyCode } from "@/lib/currency-data"
import { ConsentProvider } from "@/context/consent/consent-provider"
import { MainLayout } from "@/components/custom/layout/main-layout"
import { TooltipProvider } from "@/components/ui/tooltip"
import { CookieBanner } from "@/components/custom/consent/cookie-banner"
import { GoogleOAuthProvider } from "@react-oauth/google"
import { GoogleOneTap } from "@/components/custom/auth/google-one-tap"
import * as Sentry from "@sentry/react"

// Pages
import LandingPage from "@/pages/LandingPage"
import DemoPage from "@/pages/DemoPage.tsx"
import PrivacyPolicy from "@/pages/PrivacyPolicy"
import TermsOfService from "@/pages/TermsOfService"
import AuthPage from "@/pages/AuthPage"
import DashboardPage from "@/pages/DashboardPage.tsx" // Import Unified Auth Page
import CalendarPage from "@/pages/CalendarPage.tsx"
import { FirePage } from "@/pages/FirePage"
import { NotificationPreferencesPage } from "@/pages/NotificationPreferencesPage"

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ""

// --- Currency Initializer ---
// One-way sync: server → local. Reads user.display_currency once on login/session
// restore and sets the local CurrencyContext. Resets on logout.
function CurrencyInitializer() {
	const { user } = useAuth()
	const { currency, setCurrency } = useCurrency()
	const initialized = useRef(false)

	useEffect(() => {
		if (!user) {
			initialized.current = false
			return
		}
		if (initialized.current) return
		initialized.current = true
		const displayCurrency = normalizeCurrencyCode(user.display_currency)
		if (isCurrencyCode(displayCurrency) && displayCurrency !== currency.code) {
			setCurrency(displayCurrency)
		}
	}, [user]) // eslint-disable-line react-hooks/exhaustive-deps

	return null
}

// --- Email Theme Syncer ---
// One-way sync: local → server. On session restore, if the user's local theme
// differs from what the server has, push it so email digests match.
function EmailThemeSyncer() {
	const { user, token } = useAuth()
	const { theme } = useTheme()
	const synced = useRef(false)

	useEffect(() => {
		if (!user || !token) {
			synced.current = false
			return
		}
		if (synced.current) return
		synced.current = true
		const resolved =
			theme === "system" ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") : theme
		if (resolved !== user.email_theme) {
			hive.updateMe({ email_theme: resolved }, token).catch(() => {})
		}
	}, [user]) // eslint-disable-line react-hooks/exhaustive-deps

	return null
}

// --- Protected Route Wrapper ---
// Checks if the user is logged in before rendering the child component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
	const { isAuthenticated, isLoading } = useAuth()
	const location = useLocation()

	if (isLoading)
		return (
			<div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
				<Loader2 className="h-8 w-8 animate-spin text-sky-500" />
				<p className="text-sm text-muted-foreground">Gathering nectar from your hives...</p>
			</div>
		)

	if (!isAuthenticated) {
		return <Navigate to="/login" state={{ from: location }} replace />
	}

	return <>{children}</>
}

// --- Redirect If Authenticated ---
// Sends logged-in users away from public-only pages (e.g. landing page)
const RedirectIfAuthenticated = ({ children }: { children: React.ReactNode }) => {
	const { isAuthenticated, isLoading } = useAuth()
	if (isLoading)
		return (
			<div className="flex items-center justify-center min-h-[60vh]">
				<Loader2 className="h-8 w-8 animate-spin text-sky-500" />
			</div>
		)
	if (isAuthenticated) return <Navigate to="/dashboard" replace />
	return <>{children}</>
}

function App() {
	return (
		<GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
			<ThemeProvider defaultTheme="system" storageKey="beediv-theme">
				<ConsentProvider>
					<CurrencyProvider>
						{/* Wrap the application with the AuthProvider so Context is available everywhere */}
						<AuthProvider>
							<Sentry.ErrorBoundary
								fallback={
									<div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8 text-center">
										<h2 className="text-xl font-semibold text-amber-600">Something went wrong</h2>
										<p className="text-muted-foreground">
											An unexpected error occurred. Our team has been notified.
										</p>
										<a href="/" className="text-sm underline text-amber-600">
											Return Home
										</a>
									</div>
								}
							>
								<TooltipProvider delayDuration={200}>
									<CurrencyInitializer />
									<EmailThemeSyncer />
									<BrowserRouter>
										<Routes>
											<Route element={<MainLayout />}>
												<Route
													path="/"
													element={
														<RedirectIfAuthenticated>
															<GoogleOneTap />
															<LandingPage />
														</RedirectIfAuthenticated>
													}
												/>

												{/* Unified Auth Routes */}
												<Route
													path="/login"
													element={
														<>
															<GoogleOneTap />
															<AuthPage />
														</>
													}
												/>
												<Route
													path="/signup"
													element={
														<>
															<GoogleOneTap />
															<AuthPage />
														</>
													}
												/>
												{/* Open Demo Route */}
												<Route path="/demo" element={<DemoPage />} />

												{/* Protected Dashboard Route */}
												<Route
													path="/dashboard"
													element={
														<ProtectedRoute>
															<DashboardPage />
														</ProtectedRoute>
													}
												/>
												<Route
													path="/dashboard/:portfolioId"
													element={
														<ProtectedRoute>
															<DashboardPage />
														</ProtectedRoute>
													}
												/>

												{/* Protected Calendar Route */}
												<Route
													path="/calendar"
													element={
														<ProtectedRoute>
															<CalendarPage />
														</ProtectedRoute>
													}
												/>

												{/* Protected FIRE Dashboard Route */}
												<Route
													path="/fire"
													element={
														<ProtectedRoute>
															<FirePage />
														</ProtectedRoute>
													}
												/>

												{/* Protected Notification Preferences Route */}
												<Route
													path="/notifications"
													element={
														<ProtectedRoute>
															<NotificationPreferencesPage />
														</ProtectedRoute>
													}
												/>

												<Route path="/privacy" element={<PrivacyPolicy />} />
												<Route path="/terms" element={<TermsOfService />} />
											</Route>
										</Routes>
										<CookieBanner />
									</BrowserRouter>
								</TooltipProvider>
							</Sentry.ErrorBoundary>
						</AuthProvider>
					</CurrencyProvider>
				</ConsentProvider>
			</ThemeProvider>
		</GoogleOAuthProvider>
	)
}

export default App
